// FILE: frontend/src/components/Dashboard.tsx
// COMPLETE ADMIN DASHBOARD - FULLY WORKING VERSION

import React, { useState, useEffect, useCallback } from 'react';

interface ExistingLoan {
  id?: string;
  bankName: string;
  amount: number;
  tenure: number;
  emi: number;
  coApplicant?: string;
}

interface CreditCard {
  id?: string;
  bankName: string;
  creditLimit: number;
  outstanding: number;
}

interface Lead {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  loanType: string;
  amount: number;
  status: string;
  notes?: string;
  company?: string;
  location?: string;
  address?: string;
  addressType?: string;
  netSalary?: number;
  grossSalary?: number;
  sideIncome?: number;
  createdAt: string;
  existingLoans?: ExistingLoan[];
  creditCards?: CreditCard[];
  documents?: Array<{
    id: string;
    fileName: string;
    fileUrl?: string;
    fileType?: string;
  }>;
  managerLead?: {
    id: string;
    status: string;
    managerNotes?: string;
    recommendedBank?: string;
    manager: {
      id: string;
      fullName: string;
      email: string;
    };
  };
}

interface Message {
  id: string;
  subject: string;
  message: string;
  senderType: 'ADMIN' | 'MANAGER' | 'BOSS';
  senderName: string;
  senderEmail: string;
  createdAt: string;
  isRead: boolean;
  isRepliedTo: boolean;
  repliedAt?: string;
  readAt?: string;
  replies?: Message[];
}

interface Manager {
  id: string;
  fullName: string;
  email: string;
  phone?: string;
  isActive: boolean;
}

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  leadId?: string;
  managerName?: string;
}

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

// ==================== LEAD FORM MODAL COMPONENT ====================
interface LeadFormModalProps {
  lead: Lead | null;
  onClose: () => void;
  onSubmit: (data: any) => void;
  loading: boolean;
}

const LeadFormModal: React.FC<LeadFormModalProps> = ({ lead, onClose, onSubmit, loading }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    loanType: 'Personal Loan',
    amount: 0,
    status: 'PENDING',
    notes: '',
    company: '',
    location: '',
    address: '',
    addressType: 'OWNED',
    netSalary: 0,
    grossSalary: 0,
    sideIncome: 0,
    existingLoans: [] as ExistingLoan[],
    creditCards: [] as CreditCard[],
    documents: [] as string[],
  });

  useEffect(() => {
    if (lead) {
      const documentStrings = lead.documents 
        ? lead.documents.map(doc => doc.fileName || '')
        : [];

      setFormData({
        fullName: lead.fullName,
        email: lead.email,
        phone: lead.phone,
        loanType: lead.loanType,
        amount: lead.amount,
        status: lead.status,
        notes: lead.notes || '',
        company: lead.company || '',
        location: lead.location || '',
        address: lead.address || '',
        addressType: lead.addressType || 'OWNED',
        netSalary: lead.netSalary || 0,
        grossSalary: lead.grossSalary || 0,
        sideIncome: lead.sideIncome || 0,
        existingLoans: lead.existingLoans || [],
        creditCards: lead.creditCards || [],
        documents: documentStrings,
      });
    }
  }, [lead]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const addExistingLoan = () => {
    setFormData({
      ...formData,
      existingLoans: [
        ...formData.existingLoans,
        { bankName: '', amount: 0, tenure: 0, emi: 0, coApplicant: '' }
      ]
    });
  };

  const removeExistingLoan = (index: number) => {
    setFormData({
      ...formData,
      existingLoans: formData.existingLoans.filter((_, i) => i !== index)
    });
  };

  const updateExistingLoan = (index: number, field: keyof ExistingLoan, value: any) => {
    const updatedLoans = [...formData.existingLoans];
    updatedLoans[index] = { ...updatedLoans[index], [field]: value };
    setFormData({ ...formData, existingLoans: updatedLoans });
  };

  const addCreditCard = () => {
    setFormData({
      ...formData,
      creditCards: [
        ...formData.creditCards,
        { bankName: '', creditLimit: 0, outstanding: 0 }
      ]
    });
  };

  const removeCreditCard = (index: number) => {
    setFormData({
      ...formData,
      creditCards: formData.creditCards.filter((_, i) => i !== index)
    });
  };

  const updateCreditCard = (index: number, field: keyof CreditCard, value: any) => {
    const updatedCards = [...formData.creditCards];
    updatedCards[index] = { ...updatedCards[index], [field]: value };
    setFormData({ ...formData, creditCards: updatedCards });
  };

  const steps = [
    { number: 1, title: 'Basic Info', icon: '📋' },
    { number: 2, title: 'Personal Info', icon: '👤' },
    { number: 3, title: 'Income Info', icon: '💰' },
    { number: 4, title: 'Loan Info', icon: '🏦' },
    { number: 5, title: 'Credit Info', icon: '💳' },
    { number: 6, title: 'Notes', icon: '📝' },
    { number: 7, title: 'Documents', icon: '📄' },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-800">
              {lead ? '✏️ Edit Lead' : '➕ Create New Lead'}
            </h2>
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-gray-600 text-2xl transition-colors"
              type="button"
            >
              ×
            </button>
          </div>

          <div className="flex items-center justify-between overflow-x-auto pb-2">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div
                  className={`flex flex-col items-center cursor-pointer transition-all ${
                    currentStep === step.number ? 'opacity-100' : 'opacity-50'
                  }`}
                  onClick={() => setCurrentStep(step.number)}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-lg transition-all ${
                      currentStep === step.number
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg scale-110'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {step.icon}
                  </div>
                  <span className="text-xs mt-1 text-center whitespace-nowrap font-medium">
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`h-0.5 flex-1 mx-2 ${currentStep > step.number ? 'bg-blue-500' : 'bg-gray-300'}`}></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <span className="text-2xl">📋</span> Basic Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    placeholder="Enter full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    placeholder="email@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    placeholder="+91 98765 43210"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Loan Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.loanType}
                    onChange={(e) => setFormData({ ...formData, loanType: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  >
                    <option value="Personal Loan">Personal Loan</option>
                    <option value="Home Loan">Home Loan</option>
                    <option value="Auto Loan">Auto Loan</option>
                    <option value="Business Loan">Business Loan</option>
                    <option value="Education Loan">Education Loan</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Loan Amount (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: Number(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    placeholder="500000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  >
                    <option value="PENDING">Pending</option>
                    <option value="REVIEW">Under Review</option>
                    <option value="APPROVED">Approved</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Personal Info */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <span className="text-2xl">👤</span> Personal Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    placeholder="Enter company name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location/City
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    placeholder="Enter city"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Address
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    placeholder="Enter complete address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address Type
                  </label>
                  <select
                    value={formData.addressType}
                    onChange={(e) => setFormData({ ...formData, addressType: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  >
                    <option value="OWNED">Owned</option>
                    <option value="RENTED">Rented</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Income Info */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <span className="text-2xl">💰</span> Income Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Net Salary (₹/month)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.netSalary}
                    onChange={(e) => setFormData({ ...formData, netSalary: Number(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    placeholder="50000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gross Salary (₹/month)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.grossSalary}
                    onChange={(e) => setFormData({ ...formData, grossSalary: Number(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    placeholder="65000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Side Income (₹/month)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.sideIncome}
                    onChange={(e) => setFormData({ ...formData, sideIncome: Number(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    placeholder="10000"
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-blue-800">
                  <strong>Total Monthly Income:</strong> ₹{(formData.netSalary + formData.sideIncome).toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          )}

          {/* Step 4: Existing Loans */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <span className="text-2xl">🏦</span> Existing Loans
                </h3>
                <button
                  type="button"
                  onClick={addExistingLoan}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all font-medium"
                >
                  + Add Loan
                </button>
              </div>

              {formData.existingLoans.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <p className="text-gray-500 mb-2">No existing loans added</p>
                  <button
                    type="button"
                    onClick={addExistingLoan}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Click to add first loan
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.existingLoans.map((loan, index) => (
                    <div key={index} className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-gray-700">Loan {index + 1}</h4>
                        <button
                          type="button"
                          onClick={() => removeExistingLoan(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 px-3 py-1 rounded transition-all"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Bank Name</label>
                          <input
                            type="text"
                            value={loan.bankName}
                            onChange={(e) => updateExistingLoan(index, 'bankName', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                            placeholder="HDFC Bank"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Loan Amount (₹)</label>
                          <input
                            type="number"
                            value={loan.amount}
                            onChange={(e) => updateExistingLoan(index, 'amount', Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                            placeholder="200000"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Tenure (months)</label>
                          <input
                            type="number"
                            value={loan.tenure}
                            onChange={(e) => updateExistingLoan(index, 'tenure', Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                            placeholder="36"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">EMI (₹/month)</label>
                          <input
                            type="number"
                            value={loan.emi}
                            onChange={(e) => updateExistingLoan(index, 'emi', Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                            placeholder="6500"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Co-Applicant (Optional)</label>
                          <input
                            type="text"
                            value={loan.coApplicant || ''}
                            onChange={(e) => updateExistingLoan(index, 'coApplicant', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                            placeholder="Co-applicant name"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {formData.existingLoans.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-sm text-orange-800">
                    <strong>Total Monthly EMI:</strong> ₹{formData.existingLoans.reduce((sum, loan) => sum + loan.emi, 0).toLocaleString('en-IN')}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 5: Credit Cards */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <span className="text-2xl">💳</span> Credit Cards
                </h3>
                <button
                  type="button"
                  onClick={addCreditCard}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all font-medium"
                >
                  + Add Card
                </button>
              </div>

              {formData.creditCards.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <p className="text-gray-500 mb-2">No credit cards added</p>
                  <button
                    type="button"
                    onClick={addCreditCard}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Click to add first card
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.creditCards.map((card, index) => (
                    <div key={index} className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-gray-700">Card {index + 1}</h4>
                        <button
                          type="button"
                          onClick={() => removeCreditCard(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 px-3 py-1 rounded transition-all"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Bank Name</label>
                          <input
                            type="text"
                            value={card.bankName}
                            onChange={(e) => updateCreditCard(index, 'bankName', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                            placeholder="ICICI Bank"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Credit Limit (₹)</label>
                          <input
                            type="number"
                            value={card.creditLimit}
                            onChange={(e) => updateCreditCard(index, 'creditLimit', Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                            placeholder="100000"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Outstanding (₹)</label>
                          <input
                            type="number"
                            value={card.outstanding}
                            onChange={(e) => updateCreditCard(index, 'outstanding', Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                            placeholder="15000"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {formData.creditCards.length > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="text-sm text-purple-800">
                    <strong>Total Credit Limit:</strong> ₹{formData.creditCards.reduce((sum, card) => sum + card.creditLimit, 0).toLocaleString('en-IN')}
                    <br />
                    <strong>Total Outstanding:</strong> ₹{formData.creditCards.reduce((sum, card) => sum + card.outstanding, 0).toLocaleString('en-IN')}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 6: Notes */}
          {currentStep === 6 && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <span className="text-2xl">📝</span> Additional Notes
              </h3>
              
              <div>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  rows={8}
                  placeholder="Add any additional notes about this lead..."
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <span>💡</span> Tips for Adding Notes
                </h4>
                <ul className="space-y-1 text-sm text-blue-800">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                    Mention any special requirements or conditions
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                    Note preferred banks or financial institutions
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                    Add timeline or urgency information
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Step 7: Documents */}
          {currentStep === 7 && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <span className="text-2xl">📄</span> Documents
              </h3>
              
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors bg-gray-50">
                <div className="text-gray-400 text-5xl mb-4">📎</div>
                <p className="text-gray-600 mb-2 font-medium">Upload documents</p>
                <p className="text-sm text-gray-500 mb-4">
                  Ownership proof, ID proof, Salary slips, Bank statements, etc.
                </p>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  className="hidden"
                  id="document-upload"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    const fileNames = files.map(f => f.name);
                    setFormData({ ...formData, documents: [...formData.documents, ...fileNames] });
                  }}
                />
                <label
                  htmlFor="document-upload"
                  className="inline-block bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg cursor-pointer hover:shadow-lg transition-all font-medium"
                >
                  Choose Files
                </label>
              </div>

              {formData.documents.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-gray-700 flex items-center gap-2">
                    <span>📋</span> Uploaded Documents ({formData.documents.length})
                  </h4>
                  <div className="space-y-2">
                    {formData.documents.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between bg-white border border-gray-200 p-3 rounded-lg hover:border-blue-300 transition-colors">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">
                            {doc.endsWith('.pdf') ? '📄' : doc.match(/\.(jpg|jpeg|png)$/i) ? '🖼️' : '📎'}
                          </span>
                          <div>
                            <p className="text-sm font-medium text-gray-700">{doc}</p>
                            <p className="text-xs text-gray-500">
                              {doc.split('.').pop()?.toUpperCase()} file
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              documents: formData.documents.filter((_, i) => i !== index)
                            });
                          }}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 px-3 py-1 rounded transition-all text-sm font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-4 pt-6 border-t border-gray-200 mt-8">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={() => setCurrentStep(currentStep - 1)}
                className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-all flex items-center justify-center gap-2"
              >
                <span>←</span> Previous
              </button>
            )}
            
            {currentStep < 7 ? (
              <button
                type="button"
                onClick={() => setCurrentStep(currentStep + 1)}
                className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
              >
                Next <span>→</span>
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-green-500 to-blue-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <>Processing...</>
                ) : (
                  <><span>✓</span> {lead ? 'Update Lead' : 'Create Lead'}</>
                )}
              </button>
            )}
            
            <button
              type="button"
              onClick={onClose}
              className="px-6 bg-gray-200 text-gray-800 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-all"
            >
              Cancel
            </button>
          </div>

          <div className="mt-4 text-center text-sm text-gray-500">
            Step {currentStep} of {steps.length}
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== MESSAGE MODAL COMPONENT ====================
interface MessageModalProps {
  message: Message | null;
  onClose: () => void;
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const MessageModal: React.FC<MessageModalProps> = ({ message, onClose, showToast }) => {
  const [subject, setSubject] = useState('');
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(false);
  const [messageDetails, setMessageDetails] = useState<any>(null);

  const token = localStorage.getItem('token');
  const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.askfinancee.com/api';


  useEffect(() => {
    if (message) {
      fetchMessageDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message?.id]);

  const fetchMessageDetails = async () => {
    if (!message) return;
    
    try {
      const response = await fetch(`${API_URL}/admin/messages/${message.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setMessageDetails(data);
    } catch (error) {
      console.error('Failed to fetch message details:', error);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/admin/messages/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          subject,
          message: messageText
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send message');
      }

      showToast('Message sent to Boss successfully! ✉️', 'success');
      setSubject('');
      setMessageText('');
      onClose();
    } catch (err: any) {
      showToast(err.message || 'Failed to send message', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (message && messageDetails) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
        <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-t-2xl z-10">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold">{messageDetails.subject}</h2>
                <p className="text-purple-100 mt-1">Conversation with Boss</p>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
                type="button"
              >
                ×
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-3xl">👨‍💼</span>
                <div>
                  <p className="font-semibold text-gray-800">You</p>
                  <p className="text-sm text-gray-600">{messageDetails.senderEmail}</p>
                </div>
              </div>
              <p className="text-gray-700 whitespace-pre-wrap">{messageDetails.message}</p>
              <p className="text-sm text-gray-500 mt-3">
                {new Date(messageDetails.createdAt).toLocaleString()}
              </p>
            </div>

            {messageDetails.replies && messageDetails.replies.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800">Boss's Replies</h3>
                {messageDetails.replies.map((reply: Message) => (
                  <div key={reply.id} className="bg-purple-50 rounded-lg p-4 ml-8">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">👑</span>
                      <div>
                        <p className="font-semibold text-gray-800">{reply.senderName}</p>
                        <p className="text-sm text-gray-600">{reply.senderEmail}</p>
                      </div>
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap">{reply.message}</p>
                    <p className="text-sm text-gray-500 mt-2">
                      {new Date(reply.createdAt).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {!messageDetails.isRepliedTo && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center text-yellow-800">
                ⏳ Waiting for Boss's reply...
              </div>
            )}
          </div>

          <div className="p-6 border-t">
            <button
              onClick={onClose}
              type="button"
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-t-2xl">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold">📨 Send Message to Boss</h2>
              <p className="text-purple-100 mt-1">Communication channel with Boss</p>
            </div>
            <button
              onClick={onClose}
              type="button"
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6">
          <form onSubmit={handleSendMessage} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                placeholder="Enter message subject"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
              <textarea
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                required
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                placeholder="Type your message here..."
              />
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50"
              >
                {loading ? 'Sending...' : '✉️ Send Message'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ==================== SEND TO MANAGER MODAL ====================
interface SendToManagerModalProps {
  lead: Lead;
  managers: Manager[];
  onClose: () => void;
  onSuccess: () => void;
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const SendToManagerModal: React.FC<SendToManagerModalProps> = ({ lead, managers, onClose, onSuccess, showToast }) => {
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem('token');
  const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.askfinancee.com/api';

  const handleSendToManager = async () => {
    if (!selectedManagerId) {
      showToast('Please select a manager', 'error');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/admin/leads/${lead.id}/send-to-manager`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ managerId: selectedManagerId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send lead to manager');
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      showToast(err.message || 'Failed to send lead to manager', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-t-2xl">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold">📤 Send to Manager</h2>
              <p className="text-purple-100 mt-1">{lead.fullName}</p>
            </div>
            <button
              onClick={onClose}
              type="button"
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Manager *
            </label>
            <select
              value={selectedManagerId}
              onChange={(e) => setSelectedManagerId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
            >
              <option value="">-- Select a Manager --</option>
              {managers.filter(m => m.isActive).map((manager) => (
                <option key={manager.id} value={manager.id}>
                  {manager.fullName} ({manager.email})
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={handleSendToManager}
              disabled={loading || !selectedManagerId}
              type="button"
              className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50"
            >
              {loading ? 'Sending...' : '📤 Send to Manager'}
            </button>
            <button
              onClick={onClose}
              type="button"
              className="px-6 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN DASHBOARD COMPONENT ====================
const Dashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'leads' | 'messages' | 'notifications'>('leads');
  const [leads, setLeads] = useState<Lead[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [showLeadDetails, setShowLeadDetails] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [showSendToManagerModal, setShowSendToManagerModal] = useState(false);
  const [leadToSend, setLeadToSend] = useState<Lead | null>(null);
  const [showLeadFormModal, setShowLeadFormModal] = useState(false);
  const [leadToEdit, setLeadToEdit] = useState<Lead | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const token = localStorage.getItem('token');
  const admin = JSON.parse(localStorage.getItem('admin') || '{}');
 const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.askfinancee.com/api';


  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info' = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 5000);
  }, []);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      if (!token) {
        console.error('No token found');
        return;
      }

      const response = await fetch(`${API_URL}/admin/leads?limit=1000`, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch leads: ${response.status}`);
      }

      const data = await response.json();
      setLeads(data.leads || []);

      const totalLeads = data.leads?.length || 0;
      const approvedLeads = data.leads?.filter((l: Lead) => l.status === 'APPROVED').length || 0;
      const pendingLeads = data.leads?.filter((l: Lead) => l.status === 'PENDING').length || 0;
      const totalAmount = data.leads?.reduce((sum: number, l: Lead) => sum + l.amount, 0) || 0;

      setStats({
        totalLeads,
        approvedLeads,
        pendingLeads,
        totalAmount
      });
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      showToast('Failed to load leads. Please refresh the page.', 'error');
    } finally {
      setLoading(false);
    }
  }, [API_URL, token, showToast]);

  const fetchMessages = useCallback(async () => {
  try {
    if (!token) return;

    const [inboxRes, sentRes, unreadRes] = await Promise.all([
      fetch(`${API_URL}/admin/messages`, { headers: { Authorization: `Bearer ${token}` } }),
      fetch(`${API_URL}/admin/messages/sent`, { headers: { Authorization: `Bearer ${token}` } }),
      fetch(`${API_URL}/admin/messages/unread/count`, { headers: { Authorization: `Bearer ${token}` } })
    ]);

    // parse safely - if response is not ok, fall back to empty array/object
    const safeParse = async (res: Response) => {
      try {
        const parsed = await res.json();
        return parsed;
      } catch (err) {
        return null;
      }
    };

    const inboxData = await safeParse(inboxRes);
    const sentData = await safeParse(sentRes);
    const unreadData = await safeParse(unreadRes);

    // Normalize different possible shapes into arrays
    const inboxMessages: Message[] = Array.isArray(inboxData)
      ? inboxData
      : Array.isArray(inboxData?.messages)
        ? inboxData.messages
        : [];

    const sentMessages: Message[] = Array.isArray(sentData)
      ? sentData
      : Array.isArray(sentData?.messages)
        ? sentData.messages
        : [];

    // Combine without duplicates and sort by createdAt
    const combined = [...inboxMessages, ...sentMessages];
    const uniqueMessages = combined
      .filter((msg, index, self) => index === self.findIndex((m) => m.id === msg.id))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    setMessages(uniqueMessages);

    // unreadCount: if API returned count, use it, else derive from messages
    if (typeof unreadData?.unreadCount === 'number') {
      setUnreadCount(unreadData.unreadCount);
    } else {
      const derived = uniqueMessages.filter(m => !m.isRead).length;
      setUnreadCount(derived);
    }
  } catch (error) {
    console.error("Failed to fetch messages:", error);
    // don't throw — keep UI usable
    setMessages(prev => prev || []);
  }
}, [API_URL, token]);



  const fetchNotifications = useCallback(async () => {
  try {
    if (!token) return;

    // Only call /admin/notifications and use unreadCount from the same response
    const notifRes = await fetch(`${API_URL}/admin/notifications`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!notifRes.ok) {
      throw new Error(`Failed to fetch notifications: ${notifRes.status}`);
    }

    const notifData = await notifRes.json();

    // notifData might look like: { notifications: [...], unreadCount: 3 }
    setNotifications(notifData.notifications || []);
    setNotificationUnreadCount(typeof notifData.unreadCount === 'number' ? notifData.unreadCount : 0);
  } catch (error) {
    console.error('Failed to fetch notifications:', error);
  }
}, [API_URL, token]);

const fetchManagers = useCallback(async () => {
  try {
    if (!token) return;

    const response = await fetch(`${API_URL}/admin/managers`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch managers: ${response.status}`);
    }

    const data = await response.json();

    // Some servers return an array directly, some return { managers: [...] }
    if (Array.isArray(data)) {
      setManagers(data);
    } else if (Array.isArray(data.managers)) {
      setManagers(data.managers);
    } else {
      setManagers([]);
    }
  } catch (error) {
    console.error('Failed to fetch managers:', error);
  }
}, [API_URL, token]);

  useEffect(() => {
    fetchLeads();
    fetchMessages();
    fetchNotifications();
    fetchManagers();

    const interval = setInterval(() => {
      fetchMessages();
      fetchNotifications();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchLeads, fetchMessages, fetchNotifications, fetchManagers]);

  const handleCreateLead = async (formData: any) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/leads`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to create lead');
      }

      showToast('✅ Lead created successfully!', 'success');
      setShowLeadFormModal(false);
      setLeadToEdit(null);
      await fetchLeads();
    } catch (error) {
      console.error('Error creating lead:', error);
      showToast('❌ Failed to create lead', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLead = async (formData: any) => {
    if (!leadToEdit) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/leads/${leadToEdit.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to update lead');
      }

      showToast('✅ Lead updated successfully!', 'success');
      setShowLeadFormModal(false);
      setLeadToEdit(null);
      await fetchLeads();
    } catch (error) {
      console.error('Error updating lead:', error);
      showToast('❌ Failed to update lead', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLead = async (leadId: string) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/leads/${leadId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete lead');
      }

      showToast('🗑️ Lead deleted successfully', 'success');
      await fetchLeads();
    } catch (error) {
      console.error('Error deleting lead:', error);
      showToast('❌ Failed to delete lead', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('admin');
    window.location.href = '/login';
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'APPROVED': return 'bg-green-100 text-green-800 border-green-300';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'REJECTED': return 'bg-red-100 text-red-800 border-red-300';
      case 'REVIEW': return 'bg-blue-100 text-blue-800 border-blue-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const filteredLeads = statusFilter === 'all' 
    ? leads 
    : leads.filter(lead => lead.status.toUpperCase() === statusFilter.toUpperCase());

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 z-[70] space-y-2">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
              toast.type === 'success' ? 'bg-green-500 text-white' :
              toast.type === 'error' ? 'bg-red-500 text-white' :
              'bg-blue-500 text-white'
            }`}
            style={{ animation: 'slide-in 0.3s ease-out' }}
          >
            <span>{toast.message}</span>
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-xl">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <span className="text-4xl">🎯</span> Admin Dashboard
              </h1>
              <p className="text-blue-100 mt-1">Welcome back, {admin.fullName}</p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  setSelectedMessage(null);
                  setShowMessageModal(true);
                }}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center gap-2"
              >
                <span>📨</span> Send Message to Boss
              </button>
              <button
                onClick={handleLogout}
                className="bg-white text-blue-600 px-6 py-2 rounded-lg font-semibold hover:bg-blue-50 transition-all shadow-md"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="max-w-7xl mx-auto px-6 -mt-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Total Leads</p>
                  <p className="text-3xl font-bold text-gray-800 mt-1">{stats.totalLeads}</p>
                </div>
                <span className="text-4xl">📊</span>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Approved</p>
                  <p className="text-3xl font-bold text-gray-800 mt-1">{stats.approvedLeads}</p>
                </div>
                <span className="text-4xl">✅</span>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-yellow-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Pending</p>
                  <p className="text-3xl font-bold text-gray-800 mt-1">{stats.pendingLeads}</p>
                </div>
                <span className="text-4xl">⏳</span>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Total Amount</p>
                  <p className="text-3xl font-bold text-gray-800 mt-1">₹{(stats.totalAmount / 10000000).toFixed(1)}Cr</p>
                </div>
                <span className="text-4xl">💰</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-lg mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('leads')}
              className={`flex-1 px-6 py-4 font-semibold transition-all ${
                activeTab === 'leads'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <span>📋</span> Leads ({leads.length})
              </span>
            </button>

            <button
              onClick={() => setActiveTab('messages')}
              className={`flex-1 px-6 py-4 font-semibold transition-all relative ${
                activeTab === 'messages'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <span>💬</span> Messages
                {unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </span>
            </button>

            <button
              onClick={() => setActiveTab('notifications')}
              className={`flex-1 px-6 py-4 font-semibold transition-all relative ${
                activeTab === 'notifications'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <span>🔔</span> Notifications
                {notificationUnreadCount > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    {notificationUnreadCount}
                  </span>
                )}
              </span>
            </button>
          </div>
        </div>

        {/* Leads Tab */}
        {activeTab === 'leads' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">All Leads</h2>
              <div className="flex items-center gap-4">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="all">All Status</option>
                  <option value="PENDING">Pending</option>
                  <option value="REVIEW">Under Review</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                </select>
                
                <button
                  onClick={() => {
                    setLeadToEdit(null);
                    setShowLeadFormModal(true);
                  }}
                  className="bg-gradient-to-r from-green-500 to-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center gap-2"
                >
                  <span>➕</span> Add New Lead
                </button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <p className="text-gray-600 mt-4">Loading leads...</p>
              </div>
            ) : filteredLeads.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No leads found</p>
                <button
                  onClick={() => {
                    setLeadToEdit(null);
                    setShowLeadFormModal(true);
                  }}
                  className="mt-4 text-blue-600 hover:text-blue-800 font-semibold"
                >
                  Create your first lead
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Contact</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Loan Type</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Date</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredLeads.map((lead) => (
                      <tr key={lead.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-4">
                          <div>
                            <p className="font-semibold text-gray-800">{lead.fullName}</p>
                            {lead.company && (
                              <p className="text-xs text-gray-500">{lead.company}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-sm text-gray-600">{lead.email}</p>
                          <p className="text-xs text-gray-500">{lead.phone}</p>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm font-medium text-gray-700">{lead.loanType}</span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm font-semibold text-gray-800">
                            ₹{lead.amount.toLocaleString('en-IN')}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusBadgeColor(lead.status)}`}>
                            {lead.status}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {new Date(lead.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => {
                                setSelectedLead(lead);
                                setShowLeadDetails(true);
                              }}
                              className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-3 py-1 rounded transition-all text-sm font-medium"
                              title="View Details"
                            >
                              👁️
                            </button>
                            <button
                              onClick={() => {
                                setLeadToEdit(lead);
                                setShowLeadFormModal(true);
                              }}
                              className="text-green-600 hover:text-green-800 hover:bg-green-50 px-3 py-1 rounded transition-all text-sm font-medium"
                              title="Edit Lead"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => {
                                setLeadToSend(lead);
                                setShowSendToManagerModal(true);
                              }}
                              className="text-purple-600 hover:text-purple-800 hover:bg-purple-50 px-3 py-1 rounded transition-all text-sm font-medium"
                              title="Send to Manager"
                            >
                              📤
                            </button>
                            <button
                              onClick={() => handleDeleteLead(lead.id)}
                              className="text-red-600 hover:text-red-800 hover:bg-red-50 px-3 py-1 rounded transition-all text-sm font-medium"
                              title="Delete Lead"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Messages</h2>
              <button
                onClick={() => {
                  setSelectedMessage(null);
                  setShowMessageModal(true);
                }}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center gap-2"
              >
                <span>📨</span> Send New Message
              </button>
            </div>
            {messages.length === 0 ? (
              <div className="text-center py-12">
                <span className="text-6xl">📭</span>
                <p className="text-gray-500 mt-4">No messages yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => {
                      setSelectedMessage(message);
                      setShowMessageModal(true);
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-800">{message.subject}</h3>
                      <span className="text-xs text-gray-500">
                        {new Date(message.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">{message.message}</p>
                    <div className="flex items-center gap-4 mt-2">
                      <span className="text-xs text-gray-500">From: {message.senderName}</span>
                      {!message.isRead && (
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                          Unread
                        </span>
                      )}
                      {message.isRepliedTo && (
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                          ✓ Replied
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Notifications</h2>
            {notifications.length === 0 ? (
              <div className="text-center py-12">
                <span className="text-6xl">🔕</span>
                <p className="text-gray-500 mt-4">No notifications</p>
              </div>
            ) : (
              <div className="space-y-4">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`border rounded-lg p-4 ${
                      notification.isRead ? 'bg-white border-gray-200' : 'bg-blue-50 border-blue-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-800">{notification.title}</h3>
                      <span className="text-xs text-gray-500">
                        {new Date(notification.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{notification.message}</p>
                    {!notification.isRead && (
                      <span className="inline-block mt-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                        NEW
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lead Form Modal */}
      {showLeadFormModal && (
        <LeadFormModal
          lead={leadToEdit}
          onClose={() => {
            setShowLeadFormModal(false);
            setLeadToEdit(null);
          }}
          onSubmit={leadToEdit ? handleUpdateLead : handleCreateLead}
          loading={loading}
        />
      )}

      {/* Message Modal */}
      {showMessageModal && (
        <MessageModal
          message={selectedMessage}
          onClose={() => {
            setShowMessageModal(false);
            setSelectedMessage(null);
            fetchMessages();
          }}
          showToast={showToast}
        />
      )}

      {/* Send to Manager Modal */}
      {showSendToManagerModal && leadToSend && (
        <SendToManagerModal
          lead={leadToSend}
          managers={managers}
          onClose={() => {
            setShowSendToManagerModal(false);
            setLeadToSend(null);
          }}
          onSuccess={() => {
            fetchLeads();
            fetchNotifications();
            showToast('Lead sent to manager successfully! 📤', 'success');
          }}
          showToast={showToast}
        />
      )}

      {/* Lead Details Modal - Simple View */}
      {showLeadDetails && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => {
          setShowLeadDetails(false);
          setSelectedLead(null);
        }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b p-6 z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-gray-800">📋 Lead Details</h3>
                <button
                  onClick={() => {
                    setShowLeadDetails(false);
                    setSelectedLead(null);
                  }}
                  type="button"
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-3">Basic Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Name:</span>
                    <p className="font-semibold">{selectedLead.fullName}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <p className="font-semibold">{selectedLead.email}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Phone:</span>
                    <p className="font-semibold">{selectedLead.phone}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Company:</span>
                    <p className="font-semibold">{selectedLead.company || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Loan Info */}
              <div className="bg-purple-50 rounded-lg p-4">
                <h4 className="font-semibold text-purple-900 mb-3">Loan Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Loan Type:</span>
                    <p className="font-semibold">{selectedLead.loanType}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Amount:</span>
                    <p className="font-semibold">₹{selectedLead.amount.toLocaleString('en-IN')}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Status:</span>
                    <p className="font-semibold">
                      <span className={`px-3 py-1 rounded-full text-xs ${getStatusBadgeColor(selectedLead.status)}`}>
                        {selectedLead.status}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Income Info */}
              {(selectedLead.netSalary || selectedLead.grossSalary || selectedLead.sideIncome) && (
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 mb-3">Income Information</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    {selectedLead.netSalary && (
                      <div>
                        <span className="text-gray-600">Net Salary:</span>
                        <p className="font-semibold">₹{selectedLead.netSalary.toLocaleString('en-IN')}</p>
                      </div>
                    )}
                    {selectedLead.grossSalary && (
                      <div>
                        <span className="text-gray-600">Gross Salary:</span>
                        <p className="font-semibold">₹{selectedLead.grossSalary.toLocaleString('en-IN')}</p>
                      </div>
                    )}
                    {selectedLead.sideIncome && (
                      <div>
                        <span className="text-gray-600">Side Income:</span>
                        <p className="font-semibold">₹{selectedLead.sideIncome.toLocaleString('en-IN')}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Existing Loans */}
              {selectedLead.existingLoans && selectedLead.existingLoans.length > 0 && (
                <div className="bg-orange-50 rounded-lg p-4">
                  <h4 className="font-semibold text-orange-900 mb-3">Existing Loans ({selectedLead.existingLoans.length})</h4>
                  <div className="space-y-3">
                    {selectedLead.existingLoans.map((loan, index) => (
                      <div key={index} className="bg-white p-3 rounded border border-orange-200">
                        <p className="font-semibold">{loan.bankName}</p>
                        <div className="grid grid-cols-3 gap-2 mt-2 text-xs text-gray-600">
                          <span>Amount: ₹{loan.amount.toLocaleString('en-IN')}</span>
                          <span>EMI: ₹{loan.emi.toLocaleString('en-IN')}</span>
                          <span>Tenure: {loan.tenure} months</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Credit Cards */}
              {selectedLead.creditCards && selectedLead.creditCards.length > 0 && (
                <div className="bg-pink-50 rounded-lg p-4">
                  <h4 className="font-semibold text-pink-900 mb-3">Credit Cards ({selectedLead.creditCards.length})</h4>
                  <div className="space-y-3">
                    {selectedLead.creditCards.map((card, index) => (
                      <div key={index} className="bg-white p-3 rounded border border-pink-200">
                        <p className="font-semibold">{card.bankName}</p>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-gray-600">
                          <span>Limit: ₹{card.creditLimit.toLocaleString('en-IN')}</span>
                          <span>Outstanding: ₹{card.outstanding.toLocaleString('en-IN')}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedLead.notes && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Notes</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedLead.notes}</p>
                </div>
              )}

              {/* Documents */}
              {selectedLead.documents && selectedLead.documents.length > 0 && (
                <div className="bg-yellow-50 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-900 mb-3">Documents ({selectedLead.documents.length})</h4>
                  <div className="space-y-2">
                    {selectedLead.documents.map((doc, index) => (
                      <div key={index} className="bg-white p-2 rounded border border-yellow-200 text-sm">
                        📄 {doc.fileName}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t">
              <button
                onClick={() => {
                  setShowLeadDetails(false);
                  setSelectedLead(null);
                }}
                type="button"
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animation */}
      <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;